// Phase 5.4 · 간편 라이브 뷰
// STEP LIVE-MANAGEMENT-UPCOMING-CHECKLIST-1
//   · 진행 중 + 현재 시각 이후 예정 방송 전부 세로 카드 노출 (오늘뿐 아니라 미래 전체).
//   · 지난 방송 · 종료 · 취소 is_deleted 는 제외.
//   · 각 방송 카드 '내부' 에 자기 schedule_id 기준 체크리스트 (5개) 를 통합 렌더
//     — 공용 체크리스트 block 은 제거.
//   · CTA LOCK: `LIVE Console` → `/seller/live-control?sid={schedule_id}`.
//
// 데이터 Source (REUSE · 신규 API 금지):
//   · GET `/api/seller/broadcasts` — 전체 방송 + 각 row 에 `linked_product_count`
//     (server.py 3754 에서 BroadcastProducts count aggregation 완료)
//   · GET `/api/seller/dashboard/ops-summary` — 오늘 판매 요약
//   · GET `/api/seller/oms/summary` — OMS 카운트
import React, { useEffect, useState } from 'react';
import { Radio, ChevronRight, CheckCircle2, Circle, ShoppingBag, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import SafeImg from '@/components/SafeImg';

// STEP LIVE-MANAGEMENT-UPCOMING-CHECKLIST-1 · 상태 판정 helper.
// `/seller/broadcasts` 는 status 를 한국어 + 영문 혼재로 저장할 수 있어 소문자화 후 비교.
const _LIVE_STATUS = new Set(['live', 'LIVE', '진행중', 'ongoing']);
const _CLOSED_STATUS = new Set(['종료', 'ended', '취소', 'cancelled', 'canceled']);
const _isLiveNow = (b) => !!b && (_LIVE_STATUS.has(b.status) || _LIVE_STATUS.has((b.status || '').toString().toLowerCase()));
const _isClosed = (b) => !!b && (_CLOSED_STATUS.has(b.status) || _CLOSED_STATUS.has((b.status || '').toString().toLowerCase()));
const _scheduledMs = (b) => (b?.scheduled_at ? new Date(b.scheduled_at).getTime() : 0);

const won = (n) => `${(Number(n) || 0).toLocaleString()}원`;

// STEP LIVE-MANAGEMENT-UPCOMING-CHECKLIST-1 · 방송별 독립 체크리스트.
// `/seller/broadcasts` 응답에 `linked_product_count` 가 포함되어 있으므로 추가 API 호출 없이
// 각 broadcast 의 실제 값으로 5개 항목을 판정한다 (방송 간 상태 섞임 방지).
function _buildChecklist(b) {
  return [
    { key: 'title',     label: '방송 제목 등록',   done: !!b?.title },
    { key: 'thumbnail', label: '대표 이미지 등록', done: !!b?.thumbnail },
    { key: 'products',  label: '판매 상품 연결',   done: (b?.linked_product_count || 0) > 0 },
    { key: 'scheduled', label: '방송 시간 설정',   done: !!b?.scheduled_at },
    { key: 'public',    label: '공개 상태 확인',   done: b?.is_public !== false },
  ];
}

export default function SimpleLive({ goTo, onOpenDetail }) {
  const [broadcasts, setBroadcasts] = useState([]);
  const [ops, setOps] = useState(null);
  const [omsCounts, setOmsCounts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // STEP LIVE-MANAGEMENT-UPCOMING-CHECKLIST-1 · `/seller/broadcasts` REUSE.
        // 오늘뿐 아니라 미래 예정 방송까지 전부 포함되어 있고 각 row 에
        // linked_product_count 가 attached 되어 있다.
        const [rAll, rOps, rOms] = await Promise.all([
          api.get('/seller/broadcasts').catch(() => ({ data: [] })),
          api.get('/seller/dashboard/ops-summary').catch(() => ({ data: {} })),
          api.get('/seller/oms/summary').catch(() => ({ data: { counts: {} } })),
        ]);
        if (!alive) return;
        const arr = Array.isArray(rAll.data) ? rAll.data : (rAll.data?.items || []);
        const nowMs = Date.now();
        // 종료 · 취소 · is_deleted 제외
        const keep = arr.filter((b) => {
          if (!b || b.is_deleted) return false;
          if (_isClosed(b)) return false;
          // 진행 중 방송은 scheduled_at 무관하게 포함
          if (_isLiveNow(b)) return true;
          // 예정 방송은 scheduled_at 이 '현재 시각 이후' 여야 포함
          const ms = _scheduledMs(b);
          return ms > 0 && ms >= nowMs;
        });
        const sorted = keep.sort((a, b) => {
          // 1. LIVE 방송 우선
          if (_isLiveNow(a) && !_isLiveNow(b)) return -1;
          if (!_isLiveNow(a) && _isLiveNow(b)) return 1;
          // 2. scheduled_at 가까운 순
          return _scheduledMs(a) - _scheduledMs(b);
        });
        setBroadcasts(sorted);
        setOps(rOps.data || {});
        setOmsCounts(rOms.data?.counts || {});
      } catch { /* ignore */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return <div className="flex items-center gap-2 text-sm text-gray-500 py-16 justify-center" data-testid="simple-live-loading"><Loader2 className="animate-spin" size={16} /> 불러오는 중…</div>;
  }

  const totalOrders = ops?.today_summary?.orders || 0;
  const paidCount = omsCounts.new_paid || 0;
  const shortageCount = (omsCounts.waiting_stock || 0) + (omsCounts.has_out_of_stock || 0);
  // 하단 '이 방송 주문 처리' 바로가기 — 가장 가까운 (상단) 방송 기준.
  const topBroadcast = broadcasts[0] || null;

  return (
    <div className="space-y-5" data-testid="simple-live-section">
      <div className="flex items-baseline justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-gray-400">라이브 판매</p>
          <h2 className="mt-1 text-2xl md:text-3xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            라이브 방송
          </h2>
          <p className="mt-1 text-xs text-gray-500">방송 준비 → 판매 → 주문 처리 순서로 진행하세요.</p>
        </div>
        <button onClick={() => goTo('live', { detail: true })} className="text-xs font-bold text-[#FF3B30] hover:underline" data-testid="simple-live-detail-btn">
          상세 관리 →
        </button>
      </div>

      {broadcasts.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-black/10 dark:border-white/10 p-8 text-center" data-testid="simple-live-empty">
          <Radio className="mx-auto text-gray-300" size={28} />
          <p className="mt-2 text-sm font-bold">진행 중이거나 예정된 방송이 없습니다.</p>
          <p className="text-xs text-gray-500 mt-1">아래 [상세 관리]에서 새 방송을 등록해주세요.</p>
          <button onClick={() => goTo('live', { detail: true, action: 'new' })} className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 bg-[#FF3B30] text-white text-sm font-bold rounded-full" data-testid="simple-live-add-btn">
            <Radio size={14} /> 새 방송 등록
          </button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="simple-live-broadcast-list">
          {broadcasts.map((b) => {
            const isLive = _isLiveNow(b);
            const checklist = _buildChecklist(b);
            const doneCount = checklist.filter((c) => c.done).length;
            return (
              <div key={b.schedule_id}
                   className={`rounded-xl border-2 p-4 transition ${
                     isLive
                       ? 'border-[#FF3B30]/60 bg-[#FF3B30]/5'
                       : 'border-black/10 dark:border-white/10 bg-white dark:bg-[#141414]'
                   }`}
                   data-testid={`simple-live-card-${b.schedule_id}`}>
                <div className="flex flex-col md:flex-row gap-4">
                  <SafeImg src={b.thumbnail} kind="broadcast" alt=""
                           className="w-full md:w-32 h-32 rounded-lg object-cover flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {isLive
                        ? <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-500 text-white text-[10px] font-black">
                            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> LIVE
                          </span>
                        : <span className="inline-block px-1.5 py-0.5 rounded bg-black/[0.06] dark:bg-white/10 text-[10px] font-black">예정</span>}
                      {b.is_public === false && (
                        <span className="px-1.5 py-0.5 rounded bg-gray-500/10 text-gray-500 text-[10px] font-bold">비공개</span>
                      )}
                    </div>
                    <p className="text-sm font-black truncate">{b.title || '(제목 없음)'}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {b.scheduled_at && format(new Date(b.scheduled_at), 'M월 d일 HH:mm', { locale: ko })}
                      {' · '}{b.platform || '-'} · {b.category || '-'}
                    </p>
                    <div className="mt-3">
                      <Link to={`/seller/live-control?sid=${b.schedule_id}`}
                            className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#FF3B30] text-white text-sm font-bold rounded-full hover:bg-[#D32F2F]"
                            data-testid={`simple-live-enter-console-${b.schedule_id}`}>
                        <Radio size={14} /> LIVE Console
                      </Link>
                    </div>
                  </div>
                </div>

                {/* STEP LIVE-MANAGEMENT-UPCOMING-CHECKLIST-1 · 방송별 내부 체크리스트.
                    공용 block 은 제거하고 각 카드 자체 데이터로 독립 계산한다. */}
                <div className="mt-4 border-t border-black/10 dark:border-white/10 pt-3"
                      data-testid={`simple-live-card-checklist-${b.schedule_id}`}>
                  <p className="text-xs font-black tracking-widest uppercase mb-2 text-gray-500">
                    방송 준비{' '}
                    <span className="text-[#FF3B30] tabular-nums"
                           data-testid={`simple-live-card-checklist-count-${b.schedule_id}`}>
                      {doneCount}/{checklist.length}
                    </span>
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-2">
                    {checklist.map((c) => (
                      <div key={c.key}
                           data-testid={`simple-live-card-checklist-${b.schedule_id}-${c.key}`}
                           className={`flex items-center gap-2 p-2 rounded border text-xs ${c.done ? 'border-emerald-400/50 bg-emerald-50/50 dark:bg-emerald-500/10' : 'border-amber-400/40 bg-amber-50/40 dark:bg-amber-500/5'}`}>
                        {c.done
                          ? <CheckCircle2 size={14} className="text-emerald-600 flex-shrink-0" />
                          : <Circle size={14} className="text-amber-600 flex-shrink-0" />}
                        <span className={`font-semibold ${c.done ? 'text-emerald-800 dark:text-emerald-200' : 'text-amber-800 dark:text-amber-200'}`}>{c.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 오늘 판매/주문 요약 (today 기준 LOCK · 이번 STEP 변경 금지) */}
      <div className="rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] p-4" data-testid="simple-live-summary">
        <p className="text-sm font-black tracking-widest uppercase mb-3">오늘 판매 요약</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MiniBox label="주문 수" value={totalOrders} suffix="건" testId="live-summary-orders" />
          <MiniBox label="결제 대기" value={ops?.today_summary?.orders && omsCounts.new_paid !== undefined ? '-' : '-'} suffix="" testId="live-summary-pending" />
          <MiniBox label="결제 완료 처리 대기" value={paidCount} suffix="건" testId="live-summary-paid" />
          <MiniBox label="재고 부족" value={shortageCount} suffix="건" testId="live-summary-shortage" />
        </div>
      </div>

      {topBroadcast && (
        <div className="flex flex-wrap gap-2">
          <button onClick={() => goTo('oms', { broadcast_id: topBroadcast.schedule_id })}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#FF3B30] text-white text-sm font-bold rounded-full"
            data-testid="simple-live-goto-orders">
            <ShoppingBag size={14} /> 이 방송 주문 처리 →
          </button>
          <button onClick={() => goTo('live', { detail: true })} className="inline-flex items-center gap-1.5 px-4 py-2 border border-black/10 dark:border-white/10 text-sm font-bold rounded-full" data-testid="simple-live-detail-bottom">
            <ChevronRight size={14} /> 상세 방송 관리
          </button>
        </div>
      )}
    </div>
  );
}

const MiniBox = ({ label, value, suffix, testId }) => (
  <div className="p-3 rounded border border-black/5 dark:border-white/5">
    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">{label}</p>
    <p className="mt-1 text-lg font-black tabular-nums" data-testid={testId}>
      {typeof value === 'number' ? value.toLocaleString() : value}
      {suffix && <span className="text-xs text-gray-400 ml-0.5">{suffix}</span>}
    </p>
  </div>
);
